<script>
    import dressing from "@intellectability/platform-common";
    import {ACTIONS} from "@intellectability/platform-common/constants";
    import {
        ExportButtons,
        FlexList,
        Icon,
        SuperFilter,
        toast,
        userSecurity
    } from "@intellectability/platform-common/sv";
    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";
    import {
        Breadcrumb,
        BreadcrumbItem, Dropdown,
        DropdownItem,
        DropdownMenu,
        DropdownToggle,
        Input,
        Label
    } from "@sveltestrap/sveltestrap";

    const hasAccess = $derived(actionCheck(userSecurity.userActions));

    const {PROVIDER_DETAILS_VIEW, PROVIDERS_LIST} = ACTIONS;

    const applicable_business_rule_names = [
        "show_prv_other_id_in_superfilter",
        "show_parent_provider_in_superfilter"
    ];

    let applicable_business_rules = $derived.by(function () {
        const rules = userSecurity?.businessRules.filter(function (rule) {
            return applicable_business_rule_names.includes(rule.rule_name);
        });
        return rules || [];
    });

    let loading = $state(true);
    let isLoggedIn = $derived(userSecurity.loggedIn === true);

    // flexlist data
    let providers = $state.raw([]);

    // for dropdowns
    let providerServices = $state.raw([]);
    let providerStatuses = $state.raw([]);
    let providerTypes = $state.raw([]);
    let parentProviders = $state.raw({
        parent_providers: [],
        parent_other_ids: []
    });

    // filter options
    let selectedProviderType = $state("");
    let selectedProviderService = $state("");
    let selectedProviderStatus = $state("");
    let selectedProviderCounty = $state("");
    let selectedProviderCity = $state("");
    let selectedProviderZip = $state("");
    let selectedProviderPhone = $state("");
    let selectedProviderName = $state("");
    let selectedOtherID = $state("");
    let selectedParentProviderFilter = $state({
        parent_provider: null,
        parent_other_id: null
    });

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        recordsFiltered: null,
        recordsTotal: null,
        pageLengthMenu: [10, 25, 50, 100, 500]
    });

    // sort
    let sort = $state({
        field: "prv_name",
        order: "asc"
    });

    // column filters take the string
    let providerTypeOptions = $derived(providerTypes.map(function (type) {
        return {name: type, value: type};
    }));

    let providerStatusOptions = $derived(providerStatuses.map(function (status) {
        return {name: status.status, value: status.status};
    }));

    // super filters take the ID
    let providerServiceOptions = $derived(providerServices.map(function (service) {
        return {name: service.svc_name, value: service.svc_key_num};
    }));

    $effect(function () {

        if (isLoggedIn && !hasAccess(PROVIDERS_LIST)) {
            const err = create_security_error("ProviderList.svelte", "ProviderList", PROVIDERS_LIST);
            dressing.api.report_security_error(err).then(() => dressing.api.logout());
        }

        if (isLoggedIn) {
            const needed_initial_data = [
                dressing.get("/providers/types").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {types} = data;
                        if (Array.isArray(types) && types.length > 0) {
                            providerTypes = types;
                        }
                        return success;
                    }
                }),
                dressing.get("/providers/statuses").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {statuses} = data;
                        if (Array.isArray(statuses) && statuses.length > 0) {
                            providerStatuses = statuses;
                        }
                        return success;
                    }
                }),
                dressing.get("/providers/services").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {services} = data;
                        if (Array.isArray(services) && services.length > 0) {
                            providerServices = services;
                        }
                        return success;
                    }
                })
            ];

            // optionally fetch parent provider data if needed per business rules
            if (applicable_business_rules.some(function (rule) {
                return rule.rule_name === "show_parent_provider_in_superfilter";
            })) {
                needed_initial_data.push(dressing.api.fetch_business_rule_data("show_parent_provider_in_superfilter")
                    .then(({data: res}) => {
                        const {success, data} = res;
                        if (success === true) {
                            parentProviders = data;
                        }
                        return success;
                    }));
            }

            Promise.allSettled(needed_initial_data)
                .then(function (resolved) {

                    const result = resolved.every(r => r.value === true);
                    if (result) {
                        return loading = false;
                    }
                    else {
                        toast.error("Error loading initial data");
                    }
                });
        }
    });

    $effect(function filter_list() {

        if (!loading) {
            const filter_query = {
                prv_name: selectedProviderName || null,
                prv_city: selectedProviderCity || null,
                prv_zip: selectedProviderZip || null,
                prv_phone: selectedProviderPhone || null,
                prv_type: selectedProviderType || null,
                prv_county: selectedProviderCounty || null,
                prv_status: selectedProviderStatus || null,
                prv_other_id: selectedOtherID || null,
                service: selectedProviderService || null,
                parent_provider: selectedParentProviderFilter.parent_provider || null,
                parent_other_id: selectedParentProviderFilter.parent_other_id || null
            };

            const sort_query = {
                sort: sort.field || null,
                order: sort.order || null
            };

            const page_query = {
                page: pagination.page || null,
                limit: pagination.limit || null
            };

            dressing.get("/providers", {
                params: Object.assign(filter_query, {...sort_query, ...page_query})
            }).then(({data: res}) => {

                const {success, data} = res;

                if (success === true) {
                    const {providers: p, recordsFiltered, recordsTotal} = data;
                    if (Array.isArray(p)) {
                        providers = p;
                        pagination.recordsFiltered = recordsFiltered;
                        pagination.recordsTotal = recordsTotal;
                    }
                }

            });
        }
    });

    function row_selected(row) {
        if (hasAccess(PROVIDER_DETAILS_VIEW)) {
            window.location.href = `/index.php?action=${PROVIDER_DETAILS_VIEW}&pid=${row.prv_id}`;
        }
        else {
            dressing.api.report_security_error(
                create_security_error("App.svelte", "ProviderList", PROVIDER_DETAILS_VIEW));
        }
    }
</script>

<div>
    <!-- Breadcrumbs -->
    <Breadcrumb divider={" "} class="breadcrumbs mb-3">
        <BreadcrumbItem>
            <a href="/index.php?action=30000">
                <Icon icon="briefcase" class="mr-1"/>
                providers
            </a>
        </BreadcrumbItem>
        <BreadcrumbItem class="mx-3">
            <Icon icon="chevron-right"/>
        </BreadcrumbItem>
        <BreadcrumbItem>
            list
        </BreadcrumbItem>
    </Breadcrumb>
    <!-- Page Title -->
    <h1>
        <Icon icon="briefcase"/>
        Service Providers
    </h1>
    <!-- Super Filter -->
    <div class="mb-4">
        <SuperFilter classes="row px-0 mx-0" onreset={() => {
                    selectedProviderType = "";
                    selectedProviderService = "";
                    selectedProviderStatus = "";
                    selectedProviderCounty = "";
                    selectedProviderCity = "";
                    selectedProviderZip = "";
                    selectedProviderPhone = "";
                    selectedProviderName = "";
                    selectedOtherID = "";
                    selectedParentProviderFilter = {
                        parent_provider: null,
                        parent_other_id: null
                    }
                }}>
            <div class="col-12 col-md-3">
                <Label class="w-100">
                    Service
                    <Dropdown direction="down">
                        <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {selectedProviderService || "All"}
                                    </span>
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem onclick={() => selectedProviderService = ""}>
                                All
                            </DropdownItem>
                            {#each providerServiceOptions as {name}}
                                <DropdownItem
                                        class={selectedProviderService === name ? "active" : ""}
                                        onclick={() => selectedProviderService = name}>
                                    {name}
                                </DropdownItem>
                            {/each}
                        </DropdownMenu>
                    </Dropdown>
                </Label>
            </div>
            {#each applicable_business_rules as rule}
                {#if rule.rule_name === "show_prv_other_id_in_superfilter"}
                    <div class="col-12 col-md-2">
                        <Label>
                            {rule.value}
                            <Input class="w-100" type="text" bind:value={selectedOtherID}/>
                        </Label>
                    </div>
                {/if}
                {#if rule.rule_name === "show_parent_provider_in_superfilter"}
                    <div class="col-12 col-md-3">
                        <Label>
                            {rule.value.split(",")[0]}
                            <Dropdown direction="down">
                                <DropdownToggle color="transparent" class="w-100" caret>
                                            <span>
                                                {selectedParentProviderFilter.parent_other_id || "All"}
                                            </span>
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem
                                            onclick={() => selectedParentProviderFilter.parent_other_id = ""}>
                                        All
                                    </DropdownItem>
                                    {#each parentProviders.parent_other_ids as parent_other_id}
                                        <DropdownItem
                                                class={selectedParentProviderFilter.parent_other_id === parent_other_id ? "active" : ""}
                                                onclick={() => selectedParentProviderFilter.parent_other_id = parent_other_id}>
                                            {parent_other_id}
                                        </DropdownItem>
                                    {/each}
                                </DropdownMenu>
                            </Dropdown>
                        </Label>
                    </div>
                    <div class="col-12 col-md-3">
                        <Label>
                            {rule.value.split(",")[1]}
                            <Dropdown direction="down">
                                <DropdownToggle color="transparent" class="w-100 text-truncate" caret>
                                            <span>
                                                {selectedParentProviderFilter.parent_provider || "All"}
                                            </span>
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem
                                            onclick={() => selectedParentProviderFilter.parent_provider = ""}>
                                        All
                                    </DropdownItem>
                                    {#each parentProviders.parent_providers as parent_provider}
                                        <DropdownItem
                                                class={selectedParentProviderFilter.parent_provider === parent_provider ? "active" : ""}
                                                onclick={() => selectedParentProviderFilter.parent_provider = parent_provider}>
                                            {parent_provider}
                                        </DropdownItem>
                                    {/each}
                                </DropdownMenu>
                            </Dropdown>
                        </Label>
                    </div>
                {/if}
            {/each}
        </SuperFilter>
    </div>
    <!-- Export Buttons -->
    <div class="d-flex w-100 justify-content-end mb-3">
        <ExportButtons show={["excel", "pdf"]}
                       href={`ajax/default/ajax.download_list.php?list=${PROVIDERS_LIST}`}/>
    </div>
    <!-- TABLE -->
    <FlexList loading={loading}
              pagerConfig={pagination}
              onpagechange={(page) => pagination.page = page}
              onsort={(field, order) => {sort = {field, order};}}
              hoverable={hasAccess(PROVIDER_DETAILS_VIEW)}
              onselection={row_selected}
              onpagelengthchange={(newLimit) => pagination.limit = newLimit}
              columns={[
        {
            name: "Provider Name",
            field: "prv_name",
            sortedBy: sort.field === "prv_name" ? sort.order : "",
            sortable: true,
            collapse: false,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedProviderName = value;
            }
        },
        {
            name: "City",
            field: "prv_city",
            sortedBy: sort.field === "prv_city" ? sort.order : "",
            sortable: true,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedProviderCity = value;
            }
        },
        {
            name: "Zip",
            field: "prv_zip",
            width: "",
            sortedBy: sort.field === "prv_zip" ? sort.order : "",
            sortable: true,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedProviderZip = value;
            }
        },
        {
            name: "Phone",
            field: "prv_phone",
            width: "",
            sortable: true,
            sortedBy: sort.field === "prv_phone" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedProviderPhone = value;
            }
        },
        {
            name: "Type",
            field: "prv_type",
            width: "",
            sortable: true,
            sortedBy: sort.field === "prv_type" ? sort.order : "",
            filterType: "select",
            collapse: false,
            filterOptions: providerTypeOptions,
            onfilter: function (value) {
                selectedProviderType = value;
            }
        },
        {
            name: "County",
            field: "prv_county",
            width: "",
            sortable: true,
            sortedBy: sort.field === "prv_county" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedProviderCounty = value;
            }
        },
        {
            name: "Status",
            field: "prv_status",
            width: "",
            sortable: true,
            sortedBy: sort.field === "prv_status" ? sort.order : "",
            filterType: "select",
            filterOptions: providerStatusOptions,
            onfilter: function (value) {
                selectedProviderStatus = value;
            }
        }
    ]}
              data={providers}>
    </FlexList>
</div>